import { CALCULATION_PROCESS_STATUS_UPDATE, clearCalculationStatus } from 'actions/designer'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { PERFORMANCE_CALCULATORS_3RD_PARTY } from 'constants/calculators'
import { showNotification } from 'react-admin'
import { StatusType } from 'reducer/designer/systemCalculation'
import { call, cancel, fork, put, take } from 'redux-saga/effects'
import { isQuickQuote } from 'util/quoteMode'
import { HorizonFetchStatus, horizonService } from 'services/calculations/horizonService'
import { CalculationQueueMap, CalculationResultParams, CalculationWarning } from './types'

export const updateCalculationProcessStatus = (uuid: string, status: StatusType) => ({
  type: CALCULATION_PROCESS_STATUS_UPDATE,
  payload: { uuid, status },
})

// Saga Effects
/**
 * Custom takeLatest that cancels previous calculations for the same system UUID
 * Ensures only one calculation process is active per system
 */
export const takeLatestBySystemUuid = (
  pattern: any,
  saga: any,
  calculationQueue: CalculationQueueMap,
  ...args: any[]
) =>
  fork(function* () {
    while (true) {
      const action = yield take(pattern)
      const { payload } = action

      if (payload?.uuid && calculationQueue[payload.uuid]) {
        yield cancel(calculationQueue[payload.uuid])
      }

      calculationQueue[payload.uuid] = yield fork(saga, ...args.concat(action))
    }
  })

/**
 * Handles calculation result by updating UI state and displaying notifications
 */
export function* handleCalculationResult(
  response: any,
  { system_uuid, system, callback, showSuccessNotification }: CalculationResultParams
) {
  // Check if the calculation was cancelled
  if (window.editor.history?.redos.length > 0) {
    throw new Error('Calculation did not finish before undo. Using previous calculation result.')
  }

  yield put(updateCalculationProcessStatus(system_uuid, 'handlingCalculationSuccess'))

  const result = yield call(() => window.SceneHelper.handleCalculationSuccess(response))

  if (!result.success) {
    throw new Error('Unable to update calculation result')
  }

  yield call(updateUIState, system, result.warnings)
  displayCalculationNotification(result.warnings, showSuccessNotification)

  yield put({
    type: 'MARK_SYSTEM_CALC_AS_COMPLETE',
    payload: { uuid: system_uuid },
  })

  if (callback) {
    yield call(() => callback(true))
  }

  return result
}

export function updateCalculationStatus(system_uuid: string, callback?: Function) {
  if (callback) callback(true)
}

export function displayCalculationNotification(warnings: any[], showSuccessNotification?: boolean) {
  if (warnings?.length || showSuccessNotification) {
    const message = warnings?.length ? 'System calculations applied with warnings' : 'System calculations applied'
    const type = warnings?.length ? 'warning' : 'info'
    window.Designer.showNotification(message, type)
  }
}

export const calculateShadingQueue = (
  uuid: string,
  useOldResultsIfAvailable: boolean = false,
  forceShading: boolean = false
) => window.ShadeHelper.calculateShadingQueue(uuid, useOldResultsIfAvailable, forceShading)

export const requestSystemCalculation = (uuid: string, full_calcs: boolean) =>
  window.SceneHelper.requestSystemCalculation(uuid, full_calcs)

export function* updateUIState(system: any, warnings: any[]) {
  yield call(() => window.WorkspaceHelper?.refreshPriceLockError(system))
  yield call(() => window.WorkspaceHelper?.refreshPaymentOptionWarnings(system))
  yield call(() => window.WorkspaceHelper?.updateEphemeralCalculationError(warnings))
}

function addWarningMetadata(warnings: CalculationWarning[], uuid: string) {
  return warnings.map(({ error_code, ...warning }) => ({
    ...warning,
    source: 'calculations',
    systemId: uuid,
  }))
}

export function* updateCalculationWarnings(warnings: CalculationWarning[], uuid?: string) {
  if (uuid) {
    const calcWarnings = addWarningMetadata(warnings, uuid)
    yield call(() => window.WorkspaceHelper?.updateEphemeralCalculationError(calcWarnings))
  }
}

export const updateUIStateSync = (system: any, warnings: any[]) => {
  window.WorkspaceHelper?.refreshPriceLockError(system)
  window.WorkspaceHelper?.refreshPaymentOptionWarnings(system)
  window.WorkspaceHelper?.updateEphemeralCalculationError(warnings)
}

export function validateAndGetSystemUuid(editor: any, system_uuid?: string): string {
  if (typeof system_uuid === 'undefined') {
    if (!editor.selectedSystem) {
      throw new Error('Create and/or Select a system to calculate.')
    }
    return editor.selectedSystem?.uuid
  }
  return system_uuid
}

export function validateSystemAvailability(editor: any, system_uuid: string): any {
  const system = editor.objectByUuid(system_uuid)
  if (!system) {
    throw new Error('calculateSystem aborted... system not found.')
  }

  system.refreshAutomatedComponents()

  const errors = system.validate()
  if (errors.length > 0) {
    throw new Error('Unable to run calculations: ' + errors.join(','))
  }

  return system
}

export function prepareSystemForCalculation(editor: any, system: any) {
  // Auto-populate generation_override if needed
  if (
    !system.generation_override &&
    editor.scene.autoFacetsGeoJson &&
    system.calculator === PERFORMANCE_CALCULATORS_3RD_PARTY
  ) {
    const moduleKwStc = system.moduleType().kw_stc
    const outputSamples = editor.scene.autoFacetsGeoJson.features.filter((f) => f.geometry.type === 'Point')
    let annual_kwh = 0

    system.getModules().forEach((m) => {
      const outputSample = window?.OsModule.getClosestOutputSample(m, outputSamples)
      annual_kwh += outputSample.properties.kwh_per_kw * moduleKwStc
    })

    editor.execute(
      new SetValueCommand(
        system,
        'generation_override',
        [annual_kwh],
        window?.Utils.generateCommandUUIDOrUseGlobal(),
        true
      )
    )
  }
}

export function* prepareSystemShading(system: any, system_uuid: string, forceShadingCalculation?: boolean) {
  const editor = window.editor

  // Non-solar quotes only carry a service-only placeholder system with no real geometry
  // or location (scene origin is lon=0/lat=0), so horizon shading is meaningless and the
  // PVGIS fetch just 400s repeatedly. Skip it for non-solar projects (GS-7418); solar
  // projects are unaffected. Reuses the same `quote_mode` definition as the nav / quote.
  const quoteMode = window.projectForm?.getState()?.values?.quote_mode
  const projectIsQuickQuote = isQuickQuote(quoteMode)

  // Check if horizon shading is needed
  const requiresHorizonShading =
    !projectIsQuickQuote &&
    !editor.sceneIsLoading &&
    !editor.scene.horizon &&
    editor.scene.horizon_mode !== 'manual'

  if (requiresHorizonShading) {
    // Change yield* to yield call()
    const horizonResult = yield call(horizonService.fetchAndStoreHorizonData, editor.scene.sceneOrigin4326)
    if (horizonResult !== HorizonFetchStatus.Success) {
      if (horizonResult === HorizonFetchStatus.InvalidCoordinates) {
        console.warn('Invalid coordinates for horizon data fetch - Skipping horizon shading application')
      } else if (horizonResult === HorizonFetchStatus.MissingProvider) {
        console.debug('Skipping horizon shading application - No provider selected in setback & design settings')
      }
    }
  }

  // Calculate shading
  yield call(calculateShadingQueue, system_uuid, true, forceShadingCalculation)

  // Verify shading is complete
  if (
    (system.calculator === 2 || system.calculator === 7 || system.calculator === 9) &&
    window.ShadeHelper.panelsMissingShading(system, false, false).length > 0
  ) {
    throw new Error(
      'Notice: Aborting SceneHelper.calculateSystem() because shading incomplete, wait until the next call'
    )
  }
}

export function updateModuleGridUI(editor: any) {
  if (editor.selected && editor.selected.type === 'OsModuleGrid') {
    editor.signals.objectAnnotationChanged.dispatch(editor.selected)
    editor.signals.shadingUpdated.dispatch()
  }
}

function extractErrorMessage(error: Error, errorJson: any, errors: any[]): string {
  if (errors?.length > 0) {
    const firstError = errors[0]
    if (typeof firstError === 'string') {
      return firstError
    }
    if (firstError?.message) {
      return firstError.message
    }
    if (firstError?.error) {
      return firstError.error
    }
  }

  const primaryErrorMessage = errorJson?.data?.details?.primary_error?.message
  if (primaryErrorMessage) {
    return primaryErrorMessage
  }

  if (errorJson?.message) {
    return errorJson.message
  }

  if (error.message) {
    return error.message
  }

  return 'Unspecified Error.'
}

export function* handleCalculationError(error: Error, callback?: Function, uuid?: string) {
  // responseData is for async errors, responseJSON is for sync errors
  const responseData = (error as any).responseData || (error as any).responseJSON

  // For async errors, responseData.responseJSON contains the parsed JSON
  // For sync errors, responseData is the jqXHR object with responseJSON property
  const errorJson = responseData?.responseJSON || responseData

  // Extract errors from multiple possible locations to match sync behavior
  const errors =
    errorJson?.data?.details?.errors || errorJson?.details?.errors || errorJson?.calculation_error_messages || []

  const detail = extractErrorMessage(error, errorJson, errors)

  console.warn('Error:' + detail)
  yield put(showNotification(window.translate('System calculations could not be applied') + ': ' + detail, 'warning'))

  yield call(updateCalculationWarnings, errors, uuid)
  if (callback) yield call(() => callback(false))
  if (uuid) yield put(clearCalculationStatus(uuid))
}

export function logCalculationMetrics(system: any, calculationStartTime: Date, full_calcs?: boolean) {
  if (calculationStartTime) {
    const calculationEndTime = new Date()
    logAmplitudeEvent('calculation_metrics', {
      calculation_time: Math.round((calculationEndTime.getTime() - calculationStartTime.getTime()) / 1000),
      payment_option_count: system?.payment_options?.length,
      integrated_payment_option_count: system?.payment_options?.filter((po) => po.integration).length,
      payment_option_integrations: Array.from(new Set(system?.payment_options?.map((po) => po.integration))),
      payment_options_calcs_version: system?.pricing?.payment_option_calcs_version || 1,
      system_uuid: system?.uuid,
      is_full_calcs: Boolean(full_calcs),
    })
  }
}
